/**
 * Client-side mirror of combat/counterStrikeResolution.js
 */

function findPlayedUnitByInstanceId(playerState, instanceId) {
  if (!playerState?.playedOrders || instanceId == null) {
    return null;
  }
  for (const zone of ['front', 'flank']) {
    const units = playerState.playedOrders[zone] || [];
    for (const unit of units) {
      if (unit && String(unit.instanceId) === String(instanceId)) {
        return unit;
      }
    }
  }
  return null;
}

function orderLooksLikeCounterStrikeDraw(order) {
  if (!order) {
    return false;
  }
  if (order.isCounterStrike === true) {
    return true;
  }
  const abilities = Array.isArray(order.abilities) ? order.abilities : [];
  if (abilities.includes('Counter Strike')) {
    return true;
  }
  const description = String(order.description || '');
  return /counter\s*strike/i.test(description);
}

function hasPendingCounterStrikeDraws(playerState) {
  const pending = playerState?.pendingCounterStrike || [];
  return pending.some(orderLooksLikeCounterStrikeDraw);
}

export function isCounterStrikeDeployTargetingActive(playerState) {
  if (!playerState?.deployTargetMode || !playerState.selectedDeployInstanceId) {
    return false;
  }
  const unit = findPlayedUnitByInstanceId(playerState, playerState.selectedDeployInstanceId);
  return unit?.isCounterStrike === true;
}

export function isCounterStrikeStrategemTargetingActive(playerState) {
  const info = playerState?.needsStrategemTarget;
  if (!info) {
    return false;
  }
  if (info.isCounterStrikeStrategem === true) {
    return true;
  }
  const strategem = (playerState.hand || []).find(
    (order) => order && String(order.instanceId) === String(info.strategemInstanceId)
  );
  return strategem?.isCounterStrikeStrategem === true || strategem?.isCounterStrike === true;
}

export function isCounterStrikeTargetingActive(playerState) {
  return (
    isCounterStrikeStrategemTargetingActive(playerState) ||
    isCounterStrikeDeployTargetingActive(playerState)
  );
}

export function isPlayerResolvingCounterStrike(playerState) {
  if (!playerState) {
    return false;
  }
  if (hasPendingCounterStrikeDraws(playerState)) {
    return true;
  }
  return isCounterStrikeTargetingActive(playerState);
}

export function isOpponentResolvingCounterStrike(gameState, userId) {
  if (!gameState?.players || !gameState.state) {
    return false;
  }
  const opponentId = gameState.players.find((id) => id !== userId);
  if (!opponentId) {
    return false;
  }
  return isPlayerResolvingCounterStrike(gameState.state[opponentId]);
}

export function isCounterStrikeStrategemOrder(order) {
  if (!order) {
    return false;
  }
  return order.isCounterStrikeStrategem === true || order.isCounterStrike === true;
}

export function isStrategemOrderType(order) {
  if (!order?.type) {
    return false;
  }
  const normalized = String(order.type).toLowerCase();
  return normalized === 'strategem';
}

/**
 * Stable keys for opponent counter-strike SFX (unit deploy, strategem targeting, strategem resolved).
 * One strategem instanceId maps to a single `strategem:` key across targeting and completedOrders.
 */
export function collectOpponentCounterStrikeSoundKeys(opponentState) {
  const keys = new Set();
  if (!opponentState) {
    return keys;
  }

  for (const zone of ['front', 'flank']) {
    const units = opponentState.playedOrders?.[zone];
    if (!Array.isArray(units)) {
      continue;
    }
    for (const unit of units) {
      if (unit?.isCounterStrike && unit.instanceId != null) {
        keys.add(`unit:${String(unit.instanceId)}`);
      }
    }
  }

  if (isCounterStrikeStrategemTargetingActive(opponentState)) {
    const strategemInstanceId = opponentState.needsStrategemTarget?.strategemInstanceId;
    if (strategemInstanceId != null) {
      keys.add(`strategem:${String(strategemInstanceId)}`);
    }
  }

  const completedOrders = opponentState.completedOrders || [];
  for (const order of completedOrders) {
    if (order?.instanceId == null || !isStrategemOrderType(order)) {
      continue;
    }
    if (isCounterStrikeStrategemOrder(order)) {
      keys.add(`strategem:${String(order.instanceId)}`);
    }
  }

  return keys;
}
